import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "@fontsource/nunito-sans/latin-400.css";
import "@fontsource/nunito-sans/latin-600.css";
import "@fontsource/nunito-sans/latin-700.css";
import "./styles/tokens.css";
import "./styles/controls.css";
import "./App.style.css";
import "./styles/totem.css";
import "./styles/mobile.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { STATE_STEP } from "./app/ducks/consts";
import {
  applyAppTime,
  fetchAppEssentials,
  fetchAppTime,
} from "./app/ducks/appStateSlice";
import { setInLoco } from "./app/ducks/shopSlice";
import { trackPageView } from "./app/ducks/tracking";
import { captureAttribution } from "./app/utils/attribution";
import { listenInstallPrompt } from "./app/utils/installApp";
import watchPublishedTime from "./app/utils/watchPublishedTime";
import LoadingWall from "./app/components/LoadingWall";
import HomeSkeleton from "./app/components/HomeSkeleton";
import ErrorWall from "./app/components/ErrorWall";
import RestingWall from "./app/components/RestingWall";
import ShopHomeScreen from "./app/screens/ShopHomeScreen";
import ProductScreen from "./app/screens/ProductScreen";
import CheckoutScreen from "./app/screens/CheckoutScreen";
import OrderStatusScreen from "./app/screens/OrderStatusScreen";
import UnknownErrorGuard from "./app/components/UnknownErrorGuard";
import useMPSDK from "./app/hooks/useMPSDK";
import useResting from "./app/hooks/useResting";
import FidelityScreen from "./app/screens/FidelityScreen";
import LoginScreen from "./app/screens/LoginScreen";
import RegisterScreen from "./app/screens/RegisterScreen";
import ConfirmationScreen from "./app/screens/ConfirmationScreen";
import useShopStyle from "./app/hooks/useShopStyle";
import useShopManifest from "./app/hooks/useShopManifest";
import InlocoStatusScreen from "./app/screens/InlocoStatusScreen";
import ProductImageScreen from "./app/screens/ProductImageScreen";
import PaymentTEFScreen from "./app/screens/PaymentTEFScreen";

const availableLocations = ["mesa", "comanda", "autoatendimento"];

// O beforeinstallprompt chega logo no boot, antes de a tela de status montar.
listenInstallPrompt();

export default function App() {
  const readyRef = useRef(false);
  const dispatch = useDispatch();
  const [params, setParams] = useState(null);
  const appState = useSelector((state) => state.appState);
  const shop = useSelector((state) => state.shop);

  const { onRestMode } = useResting();

  const mercadoPagoKey = shop ? shop.mercadoPagoKey : null;
  useMPSDK(mercadoPagoKey);

  useShopStyle(shop);
  useShopManifest(shop, params ? params.restaurant : null);

  useEffect(() => {
    const [, restaurant, orderLocation, tableId] =
      window.location.pathname.split("/");
    const isInLoco = availableLocations.includes(orderLocation);
    const isSelfOrder = orderLocation === "autoatendimento";
    // antes da primeira navegação: o history.replace do useGoTo apaga a query
    captureAttribution(restaurant, {
      search: window.location.search,
      href: window.location.href,
      referrer: document.referrer,
    });
    const orderLocationPath = isInLoco ? `/:orderLocation/:mesaId` : "";
    const finalPath = `/:namespace${orderLocationPath}`;
    setParams({
      restaurant,
      orderLocation,
      tableId,
      isInLoco,
      isSelfOrder,
      finalPath,
    });
  }, []);

  useEffect(() => {
    if (params === null) {
      return;
    }

    const routeStatus = window.location.pathname.includes("status");
    dispatch(setInLoco(params));
    dispatch(
      fetchAppEssentials(params.restaurant, params.isInLoco, routeStatus),
    );
  }, [dispatch, params]);

  useEffect(() => {
    const routeStatus = window.location.pathname.includes("status");
    if (!params || !params.isSelfOrder || routeStatus) {
      return;
    }

    // Republicação do cardápio: stream do RTDB, com polling quando ele cai.
    const namespace = params.restaurant;
    const naoDerruba = (promessa) => promessa.catch(console.error);
    return watchPublishedTime(namespace, {
      onTime: (time) => naoDerruba(dispatch(applyAppTime(namespace, time))),
      poll: () => naoDerruba(dispatch(fetchAppTime(namespace))),
    });
  }, [dispatch, params]);

  if (
    appState.step === STATE_STEP.INITIAL ||
    appState.step === STATE_STEP.LOADING ||
    params === null
  ) {
    return isMobileHomePath() ? <HomeSkeleton /> : <LoadingWall />;
  }

  if (appState.step === STATE_STEP.ERROR) {
    return <ErrorWall error={appState.error} />;
  }

  if (onRestMode) {
    return <RestingWall />;
  }

  if (readyRef.current === false) {
    if (window.posNativeInterface) window.posNativeInterface.loadCompleted();
    readyRef.current = true;
  }

  const { finalPath } = params;
  return (
    <UnknownErrorGuard>
      <BrowserRouter>
        <ToastContainer />
        <PageViewTracker />
        <Routes>
          <Route
            path={`${finalPath}/status-inloco`}
            element={<InlocoStatusScreen />}
          />
          <Route
            path={`${finalPath}/status/:shortReference`}
            element={<OrderStatusScreen />}
          />
          <Route
            path={`${finalPath}/product/:productId/order/:orderUuid`}
            element={<ProductScreen />}
          />
          <Route
            path={`${finalPath}/product/:productId/image`}
            element={<ProductImageScreen />}
          />
          <Route
            path={`${finalPath}/product/:productId`}
            element={<ProductScreen />}
          />
          <Route path={`${finalPath}/checkout`} element={<CheckoutScreen />} />
          <Route path={`${finalPath}/fidelity`} element={<FidelityScreen />} />
          <Route path={`${finalPath}/login`} element={<LoginScreen />} />
          <Route path={`${finalPath}/register`} element={<RegisterScreen />} />
          <Route
            path={`${finalPath}/confirmation`}
            element={<ConfirmationScreen />}
          />
          <Route
            path={`${finalPath}/paymentTEF/:shortReference`}
            element={<PaymentTEFScreen />}
          />
          {/* A v5 casava por prefixo; na v7 só o /* pega o que sobrar da loja. */}
          <Route path={`${finalPath}/*`} element={<ShopHomeScreen />} />

          <Route path="*" element={<ErrorWall />} />
        </Routes>
      </BrowserRouter>
    </UnknownErrorGuard>
  );
}

// page_view virtual para o GTM a cada troca de rota da SPA.
function PageViewTracker() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  useEffect(() => {
    dispatch(trackPageView());
  }, [dispatch, pathname]);
  return null;
}

// O esqueleto só faz sentido na home do celular: numa rota de produto ou
// status ele mostraria a página errada, e o autoatendimento tem tela própria.
function isMobileHomePath() {
  const [, restaurant, orderLocation] = window.location.pathname.split("/");
  if (!restaurant || orderLocation === "autoatendimento") return false;
  const depth = window.location.pathname.split("/").filter(Boolean).length;
  return depth === (availableLocations.includes(orderLocation) ? 3 : 1);
}
